import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { X, Calendar, Clock, Video, Phone, MessageCircle, MapPin, CreditCard, Shield, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BookSessionModal = ({ isOpen, onClose, coach }) => {
  const [step, setStep] = useState(1);
  const [sessionDetails, setSessionDetails] = useState({
    date: '',
    time: '',
    format: '',
    duration: 60,
    notes: '',
    paymentMethod: 'platform'
  });
  const { toast } = useToast();

  if (!coach) return null;

  const sessionFormats = [
    { id: 'video', name: 'Video Call', icon: Video, description: 'Face-to-face session via secure video' },
    { id: 'phone', name: 'Phone Call', icon: Phone, description: 'Voice-only session via phone' },
    { id: 'messaging', name: 'Live Messaging', icon: MessageCircle, description: 'Real-time text-based session' },
    { id: 'in-person', name: 'In-Person', icon: MapPin, description: 'Meet in coach\'s office (if available)' }
  ];

  const availableDates = [
    { date: '2025-02-15', day: 'Today', times: ['2:00 PM', '4:30 PM'] },
    { date: '2025-02-16', day: 'Tomorrow', times: ['9:00 AM', '11:00 AM', '3:00 PM', '6:00 PM'] },
    { date: '2025-02-17', day: 'Friday', times: ['10:00 AM', '1:00 PM', '4:00 PM'] },
    { date: '2025-02-18', day: 'Saturday', times: ['9:00 AM', '2:00 PM'] }
  ];

  const durations = [
    { value: 30, label: '30 minutes', price: coach.hourlyRate * 0.5 },
    { value: 60, label: '1 hour', price: coach.hourlyRate },
    { value: 90, label: '1.5 hours', price: coach.hourlyRate * 1.5 }
  ];

  const handleBooking = () => {
    // Simulate booking process
    toast({
      title: "🎉 Session Booked Successfully!",
      description: `Your ${sessionDetails.duration}-minute session with ${coach.name} is confirmed for ${sessionDetails.date} at ${sessionDetails.time}.`,
      duration: 8000,
    });

    // Reset and close
    setStep(1);
    setSessionDetails({
      date: '',
      time: '',
      format: '',
      duration: 60,
      notes: '',
      paymentMethod: 'platform'
    });
    onClose();
  };

  const getSelectedPrice = () => {
    return durations.find(d => d.value === sessionDetails.duration)?.price || coach.hourlyRate;
  };

  const getPlatformFee = () => {
    return Math.round(getSelectedPrice() * 0.15); // 15% platform fee
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Book Session with {coach.name}</span>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress Indicator */}
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                  step >= stepNum 
                    ? 'bg-rose-500 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {stepNum}
                </div>
                {stepNum < 3 && (
                  <div className={`w-12 h-1 mx-2 ${
                    step > stepNum ? 'bg-rose-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Select Date & Time</h3>
              
              <div className="grid gap-4">
                {availableDates.map((dateOption) => (
                  <div key={dateOption.date} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-3">{dateOption.day}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {dateOption.times.map((time) => (
                        <Button
                          key={time}
                          variant={
                            sessionDetails.date === dateOption.date && sessionDetails.time === time
                              ? 'default'
                              : 'outline'
                          }
                          size="sm"
                          className={
                            sessionDetails.date === dateOption.date && sessionDetails.time === time
                              ? 'bg-rose-500 hover:bg-rose-600 text-white'
                              : 'border-rose-300 text-rose-600 hover:bg-rose-50'
                          }
                          onClick={() => {
                            setSessionDetails(prev => ({
                              ...prev,
                              date: dateOption.date,
                              time: time
                            }));
                          }}
                        >
                          {time}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <h4 className="font-medium text-gray-800 mb-3">Session Duration</h4>
                <div className="grid grid-cols-3 gap-3">
                  {durations.map((duration) => (
                    <Button
                      key={duration.value}
                      variant={sessionDetails.duration === duration.value ? 'default' : 'outline'}
                      className={
                        sessionDetails.duration === duration.value
                          ? 'bg-rose-500 hover:bg-rose-600 text-white'
                          : 'border-rose-300 text-rose-600 hover:bg-rose-50'
                      }
                      onClick={() => {
                        setSessionDetails(prev => ({
                          ...prev,
                          duration: duration.value
                        }));
                      }}
                    >
                      <div className="text-center">
                        <div className="font-medium">{duration.label}</div>
                        <div className="text-sm">${duration.price}</div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Session Format & Details</h3>
              
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Choose Session Format</h4>
                <div className="grid gap-3">
                  {sessionFormats.filter(format => coach.formats.includes(format.id)).map((format) => {
                    const Icon = format.icon;
                    return (
                      <button
                        key={format.id}
                        onClick={() => {
                          setSessionDetails(prev => ({
                            ...prev,
                            format: format.id
                          }));
                        }}
                        className={`flex items-center space-x-4 p-4 border-2 rounded-lg transition-all ${
                          sessionDetails.format === format.id
                            ? 'border-rose-500 bg-rose-50'
                            : 'border-gray-200 hover:border-rose-300 hover:bg-rose-50'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${
                          sessionDetails.format === format.id ? 'text-rose-600' : 'text-gray-600'
                        }`} />
                        <div className="flex-1 text-left">
                          <h5 className={`font-medium ${
                            sessionDetails.format === format.id ? 'text-rose-800' : 'text-gray-800'
                          }`}>
                            {format.name}
                          </h5>
                          <p className="text-sm text-gray-600">{format.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-800 mb-2">Session Notes (Optional)</h4>
                <Textarea
                  placeholder="Share what you'd like to focus on during your session..."
                  value={sessionDetails.notes}
                  onChange={(e) => {
                    setSessionDetails(prev => ({
                      ...prev,
                      notes: e.target.value
                    }));
                  }}
                  className="border-rose-200 focus:ring-rose-300 focus:border-rose-300"
                  rows={3}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Review & Payment</h3>
              
              {/* Session Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-3">Session Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Coach:</span>
                    <span className="font-medium">{coach.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Date & Time:</span>
                    <span className="font-medium">{sessionDetails.date} at {sessionDetails.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">{sessionDetails.duration} minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Format:</span>
                    <span className="font-medium capitalize">{sessionDetails.format?.replace('-', ' ')}</span>
                  </div>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3">Pricing Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Session Fee:</span>
                    <span>${getSelectedPrice()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Platform Fee (15%):</span>
                    <span>${getPlatformFee()}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-medium text-lg">
                    <span>Total:</span>
                    <span>${getSelectedPrice() + getPlatformFee()}</span>
                  </div>
                </div>
              </div>

              {/* Security Notice */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-800 mb-1">Secure Platform Guarantee</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Payment is securely processed and held in escrow</li>
                      <li>• Coach is paid only after successful session completion</li>
                      <li>• All sessions are conducted within our monitored platform</li>
                      <li>• Full refund if session quality doesn't meet standards</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Anti-Poaching Warning */}
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800 mb-1">Platform Policy</h4>
                    <p className="text-sm text-amber-700">
                      To maintain trust and security, all communications and transactions must occur within our platform. 
                      Attempts to move sessions off-platform may result in account suspension.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-6 border-t">
            {step > 1 && (
              <Button
                onClick={() => setStep(step - 1)}
                variant="outline"
                className="border-gray-300"
              >
                Previous
              </Button>
            )}
            
            <div className="flex space-x-3 ml-auto">
              <Button onClick={onClose} variant="outline" className="border-gray-300">
                Cancel
              </Button>
              
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={
                    (step === 1 && (!sessionDetails.date || !sessionDetails.time)) ||
                    (step === 2 && !sessionDetails.format)
                  }
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                >
                  Continue
                </Button>
              ) : (
                <Button
                  onClick={handleBooking}
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Confirm & Pay ${getSelectedPrice() + getPlatformFee()}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookSessionModal;